import { useCallback, useState } from 'react';
import { Coins, ChevronDown, ChevronRight } from 'lucide-react';
import { periodReportApi } from '@/api/reportCentre.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  firstOfMonth, today, fmtDate, fmtAmtZero, EmptyState,
} from '@/components/analytics/AnalyticsKit';
import {
  Pill, Pager, Dash, TabBar, InfoBanner, errMessage, DEFAULT_PAGE_SIZE, PILL_TONE_CLS,
} from './ReportCentreKit';
import type { PillTone } from './ReportCentreKit';
import type {
  FcBalancesReportDto, FxGainLossFilter, FxGainLossReportDto,
  RevaluationRegisterDto, RevaluationRunRow,
} from '@/types/reportCentre.types';

/** Amount chip for the FX gain/loss summary strip. */
const AmountChip = ({ label, amount, tone }: { label: string; amount: number; tone: PillTone }) => (
  <span className={cn(
    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap',
    PILL_TONE_CLS[tone],
  )}>
    {label}
    <span className="font-bold tabular-nums">{fmtAmtZero(amount)}</span>
  </span>
);

const REPORT_KEY = 'CURRENCY_REPORTS';

const FX_ROLE_TONES: Record<string, PillTone> = {
  FX_GAIN:            'green',
  FX_LOSS:            'red',
  UNREALIZED_FX_GAIN: 'blue',
  UNREALIZED_FX_LOSS: 'amber',
};

/** Rates carry 6 decimals server-side. */
function fmtRate(n: number | null): string {
  if (n === null) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 6 });
}

// ── Export ────────────────────────────────────────────────────────────────────

const FC_COLUMNS: ExcelColumn[] = [
  { key: 'Code',     header: 'Account Code',     width: 14 },
  { key: 'Name',     header: 'Account Name',     width: 28 },
  { key: 'Ccy',      header: 'Currency',         width: 10 },
  { key: 'FDr',      header: 'Foreign Dr',       width: 15, numFmt: '#,##0.00' },
  { key: 'FCr',      header: 'Foreign Cr',       width: 15, numFmt: '#,##0.00' },
  { key: 'FNet',     header: 'Foreign Net',      width: 15, numFmt: '#,##0.00' },
  { key: 'BNet',     header: 'Base Net',         width: 15, numFmt: '#,##0.00' },
  { key: 'Implied',  header: 'Implied Rate',     width: 13 },
  { key: 'Latest',   header: 'Latest Rate',      width: 13 },
  { key: 'Reval',    header: 'Revalued Base',    width: 15, numFmt: '#,##0.00' },
  { key: 'Delta',    header: 'Unrealized Delta', width: 16, numFmt: '#,##0.00' },
];

const FX_COLUMNS: ExcelColumn[] = [
  { key: 'Journal',   header: 'Journal',     width: 18 },
  { key: 'Date',      header: 'Posting Date',width: 12 },
  { key: 'Account',   header: 'Account',     width: 14 },
  { key: 'Name',      header: 'Name',        width: 26 },
  { key: 'Role',      header: 'Role',        width: 20 },
  { key: 'Debit',     header: 'Debit',       width: 15, numFmt: '#,##0.00' },
  { key: 'Credit',    header: 'Credit',      width: 15, numFmt: '#,##0.00' },
  { key: 'Net',       header: 'Net Effect',  width: 15, numFmt: '#,##0.00' },
  { key: 'Module',    header: 'Source',      width: 14 },
  { key: 'SourceDoc', header: 'Source Doc',  width: 18 },
  { key: 'Narration', header: 'Narration',   width: 36 },
];

const REVAL_COLUMNS: ExcelColumn[] = [
  { key: 'Period',   header: 'Period',        width: 12 },
  { key: 'RunDate',  header: 'Run Date',      width: 12 },
  { key: 'Journal',  header: 'Journal',       width: 18 },
  { key: 'Reversal', header: 'Reversal',      width: 18 },
  { key: 'Total',    header: 'Adjustment',    width: 16, numFmt: '#,##0.00' },
  { key: 'By',       header: 'Executed By',   width: 18 },
];

// ── Component ─────────────────────────────────────────────────────────────────

export const CurrencyReportsPage = () => {
  const { hasPermission } = usePermission();

  const [tab, setTab] = useState('fc');

  // Tab 1 — FC balances
  const [asAt, setAsAt] = useState(today());
  const [fc, setFc] = useState<FcBalancesReportDto | null>(null);
  const [fcLoading, setFcLoading] = useState(false);
  const [fcError, setFcError] = useState<string | null>(null);

  // Tab 2 — FX gain/loss
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [fx, setFx] = useState<FxGainLossReportDto | null>(null);
  const [fxApplied, setFxApplied] = useState<FxGainLossFilter | null>(null);
  const [fxLoading, setFxLoading] = useState(false);
  const [fxError, setFxError] = useState<string | null>(null);

  // Tab 3 — revaluation register
  const [reval, setReval] = useState<RevaluationRegisterDto | null>(null);
  const [revalLoading, setRevalLoading] = useState(false);
  const [revalError, setRevalError] = useState<string | null>(null);
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(new Set());

  const runFc = useCallback(async () => {
    setFcLoading(true);
    setFcError(null);
    try {
      const res = await periodReportApi.getFcBalances({ asAt });
      setFc(res.data?.data ?? null);
    } catch (e: unknown) {
      setFcError(errMessage(e));
    } finally {
      setFcLoading(false);
    }
  }, [asAt]);

  const fetchFx = useCallback(async (filter: FxGainLossFilter) => {
    setFxLoading(true);
    setFxError(null);
    try {
      const res = await periodReportApi.getFxGainLoss(filter);
      setFx(res.data?.data ?? null);
      setFxApplied(filter);
    } catch (e: unknown) {
      setFxError(errMessage(e));
    } finally {
      setFxLoading(false);
    }
  }, []);

  const runFx = useCallback(() => {
    void fetchFx({ fromDate, toDate, pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
  }, [fromDate, toDate, fetchFx]);

  const runReval = useCallback(async () => {
    setRevalLoading(true);
    setRevalError(null);
    try {
      const res = await periodReportApi.getRevaluationRegister();
      setReval(res.data?.data ?? null);
      setExpandedRuns(new Set());
    } catch (e: unknown) {
      setRevalError(errMessage(e));
    } finally {
      setRevalLoading(false);
    }
  }, []);

  const toggleRun = (journalId: string) => {
    setExpandedRuns(prev => {
      const next = new Set(prev);
      if (next.has(journalId)) next.delete(journalId);
      else next.add(journalId);
      return next;
    });
  };

  if (!hasPermission(Permissions.RPT_CURRENCY)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const fcRows = fc?.rows ?? [];
  const fxRows = fx?.items ?? [];
  const revalRuns = reval?.runs ?? [];
  const fxNotConfigured = fx?.status === 'NOT_CONFIGURED';
  const fxTotalPages = fx ? Math.max(1, Math.ceil(fx.totalCount / fx.pageSize)) : 1;

  const hasData = tab === 'fc' ? !!fc : tab === 'fx' ? !!fx : !!reval;
  const rowCount = tab === 'fc' ? fcRows.length : tab === 'fx' ? fxRows.length : revalRuns.length;

  const handleExcel = () => {
    if (tab === 'fc' && fc) {
      exportToExcel({
        sheetName: 'FC Balances',
        columns: FC_COLUMNS,
        rows: fcRows.map(r => ({
          Code: r.accountCode, Name: r.accountName, Ccy: r.currencyCode,
          FDr: r.foreignDebit, FCr: r.foreignCredit, FNet: r.foreignNet, BNet: r.baseNet,
          Implied: r.impliedRate ?? '', Latest: r.latestRate ?? '',
          Reval: r.revaluedBase ?? '', Delta: r.unrealizedDelta ?? '',
        })),
        title: 'Foreign-Currency Balances',
        subtitle: `${fc.companyName} · Base ${fc.baseCurrencyCode} · As at ${fc.asAt}`,
      }, `FcBalances_${fc.asAt}`);
    } else if (tab === 'fx' && fx) {
      exportToExcel({
        sheetName: 'FX Gain Loss',
        columns: FX_COLUMNS,
        rows: fxRows.map(r => ({
          Journal: r.journalNumber, Date: r.postingDate, Account: r.accountCode, Name: r.accountName,
          Role: r.role, Debit: r.debit, Credit: r.credit, Net: r.netEffect,
          Module: r.sourceModule ?? '', SourceDoc: r.sourceDocumentNo ?? '', Narration: r.narration ?? '',
        })),
        title: 'FX Gain / Loss Register',
        subtitle: `${fx.companyName} · ${fx.fromDate} – ${fx.toDate}`,
      }, `FxGainLoss_${fromDate}_${toDate}`);
    } else if (tab === 'reval' && reval) {
      exportToExcel({
        sheetName: 'Revaluation Register',
        columns: REVAL_COLUMNS,
        rows: revalRuns.map(r => ({
          Period: r.periodCode, RunDate: r.runDate, Journal: r.journalNumber,
          Reversal: r.reversalJournalNumber ?? '', Total: r.adjustmentTotal, By: r.executedByName ?? '',
        })),
        title: 'Currency Revaluation Register',
        subtitle: reval.companyName,
      }, 'RevaluationRegister');
    }
  };

  const handleCsv = () => {
    if (tab === 'fc' && fc) {
      exportToCsvRows(FC_COLUMNS, fcRows.map(r => ({
        Code: r.accountCode, Name: r.accountName, Ccy: r.currencyCode,
        FDr: r.foreignDebit, FCr: r.foreignCredit, FNet: r.foreignNet, BNet: r.baseNet,
        Implied: r.impliedRate ?? '', Latest: r.latestRate ?? '',
        Reval: r.revaluedBase ?? '', Delta: r.unrealizedDelta ?? '',
      })), `FcBalances_${fc.asAt}`);
    } else if (tab === 'fx' && fx) {
      exportToCsvRows(FX_COLUMNS, fxRows.map(r => ({
        Journal: r.journalNumber, Date: r.postingDate, Account: r.accountCode, Name: r.accountName,
        Role: r.role, Debit: r.debit, Credit: r.credit, Net: r.netEffect,
        Module: r.sourceModule ?? '', SourceDoc: r.sourceDocumentNo ?? '', Narration: r.narration ?? '',
      })), `FxGainLoss_${fromDate}_${toDate}`);
    } else if (tab === 'reval' && reval) {
      exportToCsvRows(REVAL_COLUMNS, revalRuns.map(r => ({
        Period: r.periodCode, RunDate: r.runDate, Journal: r.journalNumber,
        Reversal: r.reversalJournalNumber ?? '', Total: r.adjustmentTotal, By: r.executedByName ?? '',
      })), 'RevaluationRegister');
    }
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Coins size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Currency Reports</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          hasData={hasData}
          reportKey={REPORT_KEY}
          rowCount={rowCount}
          filterSummary={tab === 'fc' ? `asAt=${asAt}` : tab === 'fx' ? `from=${fromDate} to=${toDate}` : 'all runs'}
        />
      </div>

      <TabBar
        tabs={[
          { key: 'fc', label: 'FC Balances' },
          { key: 'fx', label: 'FX Gain/Loss' },
          { key: 'reval', label: 'Revaluation Register' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {/* ── Tab 1 — FC balances ── */}
      {tab === 'fc' && (
        <>
          <div className={cn(CARD_CLS, 'p-4')}>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="w-48">
                <label className={LABEL_CLS}>As At *</label>
                <input type="date" value={asAt} onChange={e => setAsAt(e.target.value)} className={INPUT_CLS} />
              </div>
              <RunReportButton onClick={() => { void runFc(); }} isLoading={fcLoading} disabled={!asAt} />
            </div>
          </div>

          {fcError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
              {fcError}
            </div>
          )}
          {fcLoading && !fc && <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>}
          {!fc && !fcLoading && (
            <EmptyState message='Pick an "As At" date and click "Run Report" to list foreign-currency balances by account and currency.' />
          )}

          {fc && (
            <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
              <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{fc.companyName}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Base currency {fc.baseCurrencyCode} · As at {fmtDate(fc.asAt)} · Generated {fmtDate(fc.generatedAt)}
                </div>
              </div>
              <div className="overflow-auto">
                <table className="w-full text-sm border-collapse min-w-[1400px]">
                  <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                    <tr>
                      <th className={TH_CLS}>Account</th>
                      <th className={TH_CLS}>Name</th>
                      <th className={cn(TH_CLS, 'w-20')}>Ccy</th>
                      <th className={TH_R_CLS}>Foreign Dr</th>
                      <th className={TH_R_CLS}>Foreign Cr</th>
                      <th className={TH_R_CLS}>Foreign Net</th>
                      <th className={TH_R_CLS}>Base Net</th>
                      <th className={TH_R_CLS}>Implied Rate</th>
                      <th className={TH_R_CLS}>Latest Rate</th>
                      <th className={TH_R_CLS}>Revalued Base</th>
                      <th className={TH_R_CLS}>Unrealized Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fcRows.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                          No foreign-currency balances as at the selected date.
                        </td>
                      </tr>
                    ) : fcRows.map(r => (
                      <tr key={`${r.accountId}-${r.currencyCode}`} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">{r.accountCode}</td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{r.accountName}</td>
                        <td className="px-3 py-2"><Pill label={r.currencyCode} tone="blue" /></td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(r.foreignDebit)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(r.foreignCredit)}</td>
                        <td className={cn(AMT_CLS, 'font-medium text-gray-900 dark:text-gray-100')}>{fmtAmtZero(r.foreignNet)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(r.baseNet)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-600 dark:text-gray-400')}>
                          {r.impliedRate === null ? <Dash /> : fmtRate(r.impliedRate)}
                        </td>
                        <td className={cn(AMT_CLS, 'text-gray-600 dark:text-gray-400')}>
                          {r.latestRate === null ? <Dash /> : fmtRate(r.latestRate)}
                        </td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>
                          {r.revaluedBase === null ? <Dash /> : fmtAmtZero(r.revaluedBase)}
                        </td>
                        <td className={cn(
                          AMT_CLS, 'font-medium',
                          r.unrealizedDelta === null ? ''
                          : r.unrealizedDelta < 0 ? 'text-red-600 dark:text-red-400'
                          : r.unrealizedDelta > 0 ? 'text-green-600 dark:text-green-400'
                          : 'text-gray-400 dark:text-gray-600',
                        )}>
                          {r.unrealizedDelta === null ? <Dash /> : fmtAmtZero(r.unrealizedDelta)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Tab 2 — FX gain/loss ── */}
      {tab === 'fx' && (
        <>
          <div className={cn(CARD_CLS, 'p-4')}>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="flex flex-wrap items-end gap-4">
                <div className="w-40">
                  <label className={LABEL_CLS}>From *</label>
                  <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className={INPUT_CLS} />
                </div>
                <div className="w-40">
                  <label className={LABEL_CLS}>To *</label>
                  <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className={INPUT_CLS} />
                </div>
              </div>
              <RunReportButton onClick={runFx} isLoading={fxLoading} disabled={!fromDate || !toDate} />
            </div>
          </div>

          {fxError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
              {fxError}
            </div>
          )}
          {fxLoading && !fx && <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>}
          {!fx && !fxLoading && (
            <EmptyState message='Set a posting-date window and click "Run Report" to list postings to the configured FX gain/loss accounts.' />
          )}

          {fx && fxNotConfigured && (
            <InfoBanner>
              {fx.note ?? 'FX gain/loss accounts are not configured for this company — no register can be produced.'}
            </InfoBanner>
          )}

          {fx && !fxNotConfigured && (
            <div className="flex flex-col gap-3">
              {fx.summary && (
                <div className="flex flex-wrap items-center gap-2">
                  <AmountChip label="Total Gain" amount={fx.summary.totalGain} tone="green" />
                  <AmountChip label="Total Loss" amount={fx.summary.totalLoss} tone="red" />
                  <AmountChip label="Net" amount={fx.summary.net} tone={fx.summary.net >= 0 ? 'green' : 'red'} />
                </div>
              )}
              <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
                <div className="overflow-auto">
                  <table className="w-full text-sm border-collapse min-w-[1400px]">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                      <tr>
                        <th className={TH_CLS}>Journal</th>
                        <th className={cn(TH_CLS, 'w-28')}>Posting Date</th>
                        <th className={TH_CLS}>Account</th>
                        <th className={TH_CLS}>Name</th>
                        <th className={TH_CLS}>Role</th>
                        <th className={TH_R_CLS}>Debit</th>
                        <th className={TH_R_CLS}>Credit</th>
                        <th className={TH_R_CLS}>Net Effect</th>
                        <th className={TH_CLS}>Source</th>
                        <th className={TH_CLS}>Source Doc</th>
                        <th className={TH_CLS}>Narration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fxRows.length === 0 ? (
                        <tr>
                          <td colSpan={11} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                            No FX gain/loss postings in the selected window.
                          </td>
                        </tr>
                      ) : fxRows.map((r, i) => (
                        <tr key={`${r.journalId}-${r.accountCode}-${i}`} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">
                            {r.journalNumber}
                            {r.isReversal && <span className="ml-1.5 align-middle"><Pill label="Reversal" tone="amber" /></span>}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDate(r.postingDate)}</td>
                          <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">{r.accountCode}</td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{r.accountName}</td>
                          <td className="px-3 py-2"><Pill label={r.role} tone={FX_ROLE_TONES[r.role] ?? 'grey'} /></td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(r.debit)}</td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(r.credit)}</td>
                          <td className={cn(
                            AMT_CLS, 'font-medium',
                            r.netEffect < 0 ? 'text-red-600 dark:text-red-400'
                            : r.netEffect > 0 ? 'text-green-600 dark:text-green-400'
                            : 'text-gray-400 dark:text-gray-600',
                          )}>
                            {fmtAmtZero(r.netEffect)}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{r.sourceModule ?? <Dash />}</td>
                          <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">{r.sourceDocumentNo ?? <Dash />}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.narration ?? <Dash />}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pager
                  pageNumber={fx.pageNumber}
                  totalPages={fxTotalPages}
                  totalCount={fx.totalCount}
                  shownCount={fxRows.length}
                  loading={fxLoading}
                  onPage={p => {
                    if (fxApplied) void fetchFx({ ...fxApplied, pageNumber: p });
                  }}
                  noun="postings"
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Tab 3 — revaluation register ── */}
      {tab === 'reval' && (
        <>
          <div className={cn(CARD_CLS, 'p-4')}>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl">
                Lists every posted currency-revaluation run for the company, newest first.
              </p>
              <RunReportButton onClick={() => { void runReval(); }} isLoading={revalLoading} />
            </div>
          </div>

          {revalError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
              {revalError}
            </div>
          )}
          {revalLoading && !reval && <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>}
          {!reval && !revalLoading && (
            <EmptyState message='Click "Run Report" to reconstruct the revaluation register from posted FX revaluation journals.' />
          )}

          {reval && (
            <div className="flex flex-col gap-3">
              <InfoBanner>
                Rows are reconstructed from posted FX revaluation journals ({reval.status}); per-currency run
                detail is not persisted and is therefore unavailable.
                {reval.note ? <> {reval.note}</> : null}
              </InfoBanner>
              <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
                <div className="overflow-auto">
                  <table className="w-full text-sm border-collapse min-w-[1000px]">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                      <tr>
                        <th className={cn(TH_CLS, 'w-8')} />
                        <th className={TH_CLS}>Period</th>
                        <th className={cn(TH_CLS, 'w-28')}>Run Date</th>
                        <th className={TH_CLS}>Journal</th>
                        <th className={TH_CLS}>Auto-Reversal</th>
                        <th className={cn(TH_CLS, 'w-24')}>Status</th>
                        <th className={TH_R_CLS}>Adjustment</th>
                        <th className={TH_CLS}>Executed By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revalRuns.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                            No FX revaluation journals found for this company.
                          </td>
                        </tr>
                      ) : revalRuns.map(r => {
                        const isOpen = expandedRuns.has(r.journalId);
                        return (
                          <RevaluationRunGroup key={r.journalId} run={r} isOpen={isOpen} onToggle={() => toggleRun(r.journalId)} />
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── Expandable revaluation run row ────────────────────────────────────────────

const RevaluationRunGroup = ({ run: r, isOpen, onToggle }: {
  run: RevaluationRunRow;
  isOpen: boolean;
  onToggle: () => void;
}) => (
  <>
    <tr
      onClick={onToggle}
      className="border-t border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
      title="Show journal lines"
    >
      <td className="px-3 py-2 text-gray-400">
        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </td>
      <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">{r.periodCode}</td>
      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDate(r.runDate)}</td>
      <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">{r.journalNumber}</td>
      <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">{r.reversalJournalNumber ?? <Dash />}</td>
      <td className="px-3 py-2"><Pill label={r.status} tone="green" /></td>
      <td className={cn(AMT_CLS, 'font-medium text-gray-900 dark:text-gray-100')}>{fmtAmtZero(r.adjustmentTotal)}</td>
      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{r.executedByName ?? <Dash />}</td>
    </tr>
    {isOpen && (
      <tr className="border-t border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/30">
        <td colSpan={8} className="px-6 py-3">
          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Journal Lines{r.lines.length >= 200 ? ' (capped at 200)' : ''}
          </div>
          <table className="w-full max-w-3xl text-xs border-collapse">
            <thead>
              <tr className="text-gray-500 dark:text-gray-400">
                <th className="py-1 pr-4 text-left font-medium">Account</th>
                <th className="py-1 pr-4 text-left font-medium">Name</th>
                <th className="py-1 pr-4 text-right font-medium">Debit</th>
                <th className="py-1 pr-4 text-right font-medium">Credit</th>
                <th className="py-1 text-left font-medium">Narration</th>
              </tr>
            </thead>
            <tbody>
              {r.lines.map((l, i) => (
                <tr key={`${l.accountCode}-${i}`} className="border-t border-gray-200/60 dark:border-gray-700/60 text-gray-700 dark:text-gray-300">
                  <td className="py-1.5 pr-4 font-mono">{l.accountCode}</td>
                  <td className="py-1.5 pr-4">{l.accountName}</td>
                  <td className="py-1.5 pr-4 text-right tabular-nums">{fmtAmtZero(l.debit)}</td>
                  <td className="py-1.5 pr-4 text-right tabular-nums">{fmtAmtZero(l.credit)}</td>
                  <td className="py-1.5">{l.narration ?? <Dash />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </td>
      </tr>
    )}
  </>
);
